// /audit — Audit Logs (PAGES.md §3.73, Sprint G3.11)
//
// Immutable audit trail of all CUD operations. Filter by entity type, action,
// user, entity-ID, date range, and (for platform roles) tenant. Detail drawer
// shows the recorded payload with sensitive fields redacted and a before/after
// diff when `new_value` is present. CSV export and share via WhatsApp/Email
// use the standard report export menu.
//
// SHARED vertical. MANAGER (30) sees their own actions only; OWNER (50) sees the
// whole tenant; MODERATOR (60)+ for cross-tenant. FR-AUD-001 to FR-AUD-008.

import { useState, useCallback, useEffect, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import {
  PageContainer,
  Badge,
  Select,
  DatePicker,
  Skeleton,
  EmptyState,
  ErrorBoundary,
  DataTable,
  Pagination,
  Drawer,
  Input,
  Button,
  useToast,
} from '@bbpos/ui';
import type { ColumnDef } from '@bbpos/ui';
import { useAuthStore } from '@/stores/use-auth-store';
import { auditService } from '@/services/audit.service';
import type { AuditEntry, AuditAction } from '@/services/audit.service';
import { userService } from '@/services/user.service';
import { tenantService } from '@/services/tenant.service';
import { ROLE_LEVELS } from '@/types/user';
import { ApiError } from '@/lib/api-client';
import { GLASS, GLASS_FILTER, cn } from '@/constants/design-tokens';
import {
  ReportExportMenu,
  downloadCsv,
  shareReportLink,
  type ReportExportFormat,
} from '@/features/reports';

// ─── Constants ─────────────────────────────────────────────────────────────

const PAGE_SIZE = 50;

const SENSITIVE_FIELDS = new Set([
  'password', 'password_hash', 'otp', 'token', 'refresh_token', 'access_token',
  'secret', 'api_key', 'authorization', 'cookie', 'credit_card', 'cvv', 'ssn', 'pin',
]);

type BadgeVariant = 'success' | 'info' | 'danger' | 'neutral';

const ACTION_BADGE: Record<AuditAction, { variant: BadgeVariant; tone: string; strip: string }> = {
  CREATE: {
    variant: 'success',
    tone: 'bg-emerald-500/15 text-emerald-200 ring-1 ring-inset ring-emerald-400/30',
    strip: 'from-emerald-500/25 via-emerald-500/10 to-transparent',
  },
  UPDATE: {
    variant: 'info',
    tone: 'bg-sky-500/15 text-sky-200 ring-1 ring-inset ring-sky-400/30',
    strip: 'from-sky-500/25 via-sky-500/10 to-transparent',
  },
  DELETE: {
    variant: 'danger',
    tone: 'bg-rose-500/15 text-rose-200 ring-1 ring-inset ring-rose-400/30',
    strip: 'from-rose-500/25 via-rose-500/10 to-transparent',
  },
  READ: {
    variant: 'neutral',
    tone: 'bg-neutral-500/15 text-neutral-300 ring-1 ring-inset ring-neutral-400/20',
    strip: 'from-neutral-500/20 via-neutral-500/5 to-transparent',
  },
};

const ACTION_FILTER_OPTIONS = [
  { value: '', label: 'All Actions' },
  { value: 'CREATE', label: 'Create' },
  { value: 'UPDATE', label: 'Update' },
  { value: 'DELETE', label: 'Delete' },
];

// Canonical singular form — matches entity_type in API response (audit.service.ts resourceMap).
const ENTITY_TYPE_OPTIONS = [
  { value: '', label: 'All Entities' },
  { value: 'BILL', label: 'Bill' },
  { value: 'PRODUCT', label: 'Product' },
  { value: 'ORDER', label: 'Order' },
  { value: 'USER', label: 'User' },
  { value: 'TENANT', label: 'Tenant' },
  { value: 'EXPENSE', label: 'Expense' },
  { value: 'LOCATION', label: 'Location' },
  { value: 'CUSTOMER', label: 'Customer' },
  { value: 'NOTIFICATION', label: 'Notification' },
  { value: 'VOUCHER', label: 'Voucher' },
  { value: 'SHIFT', label: 'Shift' },
  { value: 'REPORT', label: 'Report' },
];

// Map entity type → admin route for the drawer "open related" link.
const ENTITY_ROUTE: Record<string, (id: string) => string> = {
  TENANT: (id) => `/tenants/${id}`,
  PRODUCT: (id) => `/products/${id}`,
  USER: (id) => `/users/${id}`,
  LOCATION: (id) => `/locations/${id}`,
  EXPENSE: (id) => `/expenses/${id}`,
  CUSTOMER: (id) => `/customers/${id}`,
};

// Role pill tone by numeric level.
function roleTone(role: string | null | undefined): string {
  const level = ROLE_LEVELS[role ?? ''] ?? 0;
  if (level >= 80) return 'bg-rose-500/10 text-rose-600 dark:text-rose-300'; // SUPER_ADMIN / ADMIN
  if (level >= 60) return 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-300'; // MODERATOR
  if (level >= 50) return 'bg-brand-500/10 text-brand-600 dark:text-brand-300'; // OWNER
  if (level >= 30) return 'bg-teal-500/10 text-teal-600 dark:text-teal-300'; // MANAGER
  return 'bg-neutral-500/10 text-neutral-600 dark:text-neutral-400'; // CASHIER / CAPTAIN
}

// ─── Icons ─────────────────────────────────────────────────────────────────

function ShieldIcon({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}

function ShieldEmptyIcon() {
  return (
    <svg className="h-14 w-14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}

function FilterIcon({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
  );
}

function CopyIcon({ className = 'h-3.5 w-3.5' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
    </svg>
  );
}

function ExternalLinkIcon({ className = 'h-3.5 w-3.5' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatTimestamp(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
  } catch { return iso; }
}

function redactValue(key: string, value: unknown): unknown {
  if (SENSITIVE_FIELDS.has(key.toLowerCase())) return '[REDACTED]';
  return value;
}

function todayRange(): { from: string; to: string } {
  const d = new Date();
  const iso = d.toISOString().split('T')[0];
  return { from: iso, to: iso };
}

function daysAgoRange(days: number): { from: string; to: string } {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - (days - 1));
  return {
    from: start.toISOString().split('T')[0],
    to: end.toISOString().split('T')[0],
  };
}

function thisMonthRange(): { from: string; to: string } {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  return {
    from: start.toISOString().split('T')[0],
    to: now.toISOString().split('T')[0],
  };
}

// ─── Payload Tree (JSON viewer with redaction) ─────────────────────────────

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function ScalarPill({ value }: { value: unknown }) {
  const str = value === null || value === undefined
    ? '—'
    : typeof value === 'string' ? value : JSON.stringify(value);
  return (
    <span className="px-1.5 py-0.5 rounded bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-200 break-all font-mono text-[11px]">
      {str}
    </span>
  );
}

function PayloadNode({ nodeKey, value, depth = 0 }: { nodeKey: string; value: unknown; depth?: number }) {
  const [open, setOpen] = useState(depth < 1);
  const redacted = redactValue(nodeKey, value);
  const isRedacted = redacted === '[REDACTED]';
  const complex = !isRedacted && (Array.isArray(value) || isPlainObject(value));

  if (!complex) {
    return (
      <div className="flex items-start gap-2 py-0.5">
        <span className="font-mono text-[11px] font-medium text-neutral-600 dark:text-neutral-400 min-w-[120px] tracking-tight">
          {nodeKey}
        </span>
        {isRedacted ? (
          <span className="italic text-[11px] text-neutral-400 dark:text-neutral-500">[REDACTED]</span>
        ) : (
          <ScalarPill value={value} />
        )}
      </div>
    );
  }

  const entries = Array.isArray(value)
    ? (value as unknown[]).map((v, i) => [String(i), v] as [string, unknown])
    : Object.entries(value as Record<string, unknown>);

  return (
    <div className="py-0.5">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1 text-[11px] font-mono font-medium text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100 transition-colors"
        aria-expanded={open}
      >
        <span className={cn('transition-transform duration-150', open ? 'rotate-90' : '')}>▸</span>
        <span>{nodeKey}</span>
        <span className="text-neutral-400 dark:text-neutral-500">
          {Array.isArray(value) ? `[${entries.length}]` : `{${entries.length}}`}
        </span>
      </button>
      {open && (
        <div className="pl-4 mt-0.5 border-l border-neutral-200 dark:border-neutral-800">
          {entries.map(([k, v]) => (
            <PayloadNode key={k} nodeKey={k} value={v} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  );
}

function PayloadTree({ payload }: { payload: Record<string, unknown> | null }) {
  const entries = useMemo(
    () => (payload ? Object.entries(payload) : []),
    [payload],
  );
  if (entries.length === 0) {
    return <p className="text-xs italic text-neutral-400 dark:text-neutral-500">No payload recorded for this entry.</p>;
  }
  return (
    <div className="space-y-0.5">
      {entries.map(([k, v]) => (
        <PayloadNode key={k} nodeKey={k} value={v} />
      ))}
    </div>
  );
}

// ─── Diff View (when new_value + changes both present) ────────────────────

function DiffPair({
  field,
  oldVal,
  newVal,
}: { field: string; oldVal: unknown; newVal: unknown }) {
  const oRedacted = redactValue(field, oldVal);
  const nRedacted = redactValue(field, newVal);
  const oIsRedacted = oRedacted === '[REDACTED]';
  const nIsRedacted = nRedacted === '[REDACTED]';
  const oStr = oIsRedacted ? '[REDACTED]' : JSON.stringify(oldVal);
  const nStr = nIsRedacted ? '[REDACTED]' : JSON.stringify(newVal);

  return (
    <div className="space-y-1">
      <p className="text-[11px] font-mono font-medium text-neutral-500 dark:text-neutral-400">{field}</p>
      <div className="grid grid-cols-2 gap-2 text-[11px] font-mono">
        <div className="rounded-lg bg-rose-50 dark:bg-rose-900/15 p-2">
          <p className="text-[10px] uppercase tracking-wider text-rose-500 dark:text-rose-300 font-semibold mb-0.5">Before</p>
          {oIsRedacted
            ? <em className="text-neutral-400 italic">[REDACTED]</em>
            : <span className="text-rose-700 dark:text-rose-200 break-all">{oStr ?? '—'}</span>}
        </div>
        <div className="rounded-lg bg-emerald-50 dark:bg-emerald-900/15 p-2">
          <p className="text-[10px] uppercase tracking-wider text-emerald-600 dark:text-emerald-300 font-semibold mb-0.5">After</p>
          {nIsRedacted
            ? <em className="text-neutral-400 italic">[REDACTED]</em>
            : <span className="text-emerald-700 dark:text-emerald-200 break-all">{nStr ?? '—'}</span>}
        </div>
      </div>
    </div>
  );
}

function DiffView({
  oldValue,
  newValue,
}: { oldValue: Record<string, unknown> | null; newValue: Record<string, unknown> | null }) {
  const keys = useMemo(() => {
    const set = new Set<string>();
    if (oldValue) Object.keys(oldValue).forEach((k) => set.add(k));
    if (newValue) Object.keys(newValue).forEach((k) => set.add(k));
    return [...set];
  }, [oldValue, newValue]);

  if (keys.length === 0) return null;

  return (
    <div className="space-y-2">
      {keys.map((key) => {
        const o = oldValue?.[key];
        const n = newValue?.[key];
        if (JSON.stringify(o) === JSON.stringify(n)) return null;
        return <DiffPair key={key} field={key} oldVal={o} newVal={n} />;
      })}
    </div>
  );
}

// ─── Mobile Card ────────────────────────────────────────────────────────────

function AuditMobileCard({ entry, onClick }: { entry: AuditEntry; onClick: () => void }) {
  const badge = ACTION_BADGE[entry.action] ?? ACTION_BADGE.READ;
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(GLASS, 'w-full p-4 animate-slide-up text-left')}
      aria-label={`Audit entry ${entry.action} ${entry.entity_type} by ${entry.user_name ?? 'unknown'}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300 truncate">{entry.user_name ?? '—'}</p>
          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5 capitalize">
            {entry.entity_type.toLowerCase()}
            {entry.entity_id ? <> · <span className="font-mono">{entry.entity_id.slice(0, 8)}…</span></> : null}
          </p>
        </div>
        <Badge variant={badge.variant} size="sm">{entry.action}</Badge>
      </div>
      <p className="text-xs font-mono tabular-nums text-neutral-400 dark:text-neutral-500 mt-2">
        {formatTimestamp(entry.created_at)}
      </p>
    </button>
  );
}

// ─── Types / Options ──────────────────────────────────────────────────────

interface UserOption { id: string; name: string }
interface TenantOption { id: string; business_name: string }

type Preset = 'today' | '7d' | '30d' | '90d' | 'month' | '';

// ─── Component ────────────────────────────────────────────────────────────

function AuditLogsContent() {
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const profile = useAuthStore((s) => s.profile);
  const roleLevel = ROLE_LEVELS[profile?.role ?? ''] ?? 0;
  const isPlatformRole = roleLevel >= 60;
  // FR-AUD-007: below OWNER (i.e. MANAGER) the API forces user_id to the caller,
  // so the page offers no user filter and says what is being shown.
  const isOwnActionsOnly = !isPlatformRole && roleLevel < ROLE_LEVELS.OWNER;

  const urlPage = Number(searchParams.get('page') ?? '1');
  const urlAction = searchParams.get('action') ?? '';
  const urlEntity = searchParams.get('entity_type') ?? '';
  const urlUser = searchParams.get('user_id') ?? '';
  const urlEntityId = searchParams.get('entity_id') ?? '';
  const urlTenant = searchParams.get('tenant_id') ?? '';
  const urlFrom = searchParams.get('from') ?? '';
  const urlTo = searchParams.get('to') ?? '';

  const [page, setPage] = useState(urlPage);
  const [actionFilter, setActionFilter] = useState(urlAction);
  const [entityFilter, setEntityFilter] = useState(urlEntity);
  const [userFilter, setUserFilter] = useState(urlUser);
  const [entityIdFilter, setEntityIdFilter] = useState(urlEntityId);
  const [tenantFilter, setTenantFilter] = useState(urlTenant);
  const [dateFrom, setDateFrom] = useState(urlFrom);
  const [dateTo, setDateTo] = useState(urlTo);
  const [activePreset, setActivePreset] = useState<Preset>('');

  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [actionCounts, setActionCounts] = useState({ creates: 0, updates: 0, deletes: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<AuditEntry | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);

  const [users, setUsers] = useState<UserOption[]>([]);
  const [tenants, setTenants] = useState<TenantOption[]>([]);

  useEffect(() => { document.title = 'Audit Logs — Orio360 Admin'; }, []);

  // URL sync
  useEffect(() => {
    const params: Record<string, string> = {};
    if (actionFilter) params.action = actionFilter;
    if (entityFilter) params.entity_type = entityFilter;
    if (userFilter) params.user_id = userFilter;
    if (entityIdFilter) params.entity_id = entityIdFilter;
    if (tenantFilter) params.tenant_id = tenantFilter;
    if (dateFrom) params.from = dateFrom;
    if (dateTo) params.to = dateTo;
    if (page > 1) params.page = String(page);
    setSearchParams(params, { replace: true });
  }, [actionFilter, entityFilter, userFilter, entityIdFilter, tenantFilter, dateFrom, dateTo, page, setSearchParams]);

  useEffect(() => {
    if (isOwnActionsOnly) return;
    let cancelled = false;
    (async () => {
      try {
        // Page through every user so the filter can offer all of them — not
        // just the first 200 — otherwise users past the cap are unselectable.
        // Guard bounds the loop if the API ever misreports totalPages.
        const collected: UserOption[] = [];
        for (let pageNum = 1, guard = 0; guard < 100; pageNum++, guard++) {
          const result = await userService.list({ page: pageNum, limit: 200 });
          if (cancelled) return;
          collected.push(...result.data.map((u) => ({ id: u.id, name: u.name })));
          if (result.data.length === 0 || pageNum >= result.meta.totalPages) break;
        }
        if (!cancelled) setUsers(collected);
      } catch { /* optional filter */ }
    })();
    return () => { cancelled = true; };
  }, [isOwnActionsOnly]);

  useEffect(() => {
    if (!isPlatformRole) return;
    let cancelled = false;
    (async () => {
      try {
        // Page through every tenant so the filter offers all of them (see users above).
        const collected: TenantOption[] = [];
        for (let pageNum = 1, guard = 0; guard < 100; pageNum++, guard++) {
          const result = await tenantService.list({ page: pageNum, limit: 200 });
          if (cancelled) return;
          collected.push(...result.data.map((t) => ({ id: t.id, business_name: t.business_name })));
          if (result.data.length === 0 || pageNum >= result.meta.totalPages) break;
        }
        if (!cancelled) setTenants(collected);
      } catch { /* optional filter */ }
    })();
    return () => { cancelled = true; };
  }, [isPlatformRole]);

  const buildFilters = useCallback(() => ({
    tenant_id: isPlatformRole && tenantFilter ? tenantFilter : undefined,
    action: actionFilter || undefined,
    entity_type: entityFilter || undefined,
    user_id: userFilter || undefined,
    entity_id: entityIdFilter || undefined,
    start_date: dateFrom || undefined,
    end_date: dateTo || undefined,
  }), [isPlatformRole, tenantFilter, actionFilter, entityFilter, userFilter, entityIdFilter, dateFrom, dateTo]);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await auditService.list({ ...buildFilters(), page });
      setEntries(result.data);
      setTotalItems(result.meta.total);
    } catch (err) {
      toast({ title: err instanceof ApiError ? err.message : 'Failed to load audit logs', variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [page, buildFilters, toast]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openDetail = useCallback((entry: AuditEntry) => {
    setSelectedEntry(entry);
    setDrawerOpen(true);
  }, []);

  const applyPreset = useCallback((p: Preset) => {
    let range: { from: string; to: string } | null = null;
    switch (p) {
      case 'today':  range = todayRange();       break;
      case '7d':     range = daysAgoRange(7);    break;
      case '30d':    range = daysAgoRange(30);   break;
      case '90d':    range = daysAgoRange(90);   break;
      case 'month':  range = thisMonthRange();   break;
      default:       range = null;               break;
    }
    setActivePreset(p);
    setDateFrom(range?.from ?? '');
    setDateTo(range?.to ?? '');
    setPage(1);
  }, []);

  const resetFilters = useCallback(() => {
    setActionFilter('');
    setEntityFilter('');
    setUserFilter('');
    setEntityIdFilter('');
    setTenantFilter('');
    setDateFrom('');
    setDateTo('');
    setActivePreset('');
    setPage(1);
  }, []);

  const copyToClipboard = useCallback(async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast({ title: `${label} copied`, variant: 'success' });
    } catch {
      toast({ title: 'Copy failed', variant: 'error' });
    }
  }, [toast]);

  // KPI counts across the whole filtered dataset (not just the current page).
  // Each count is the `meta.total` of the same filters narrowed to one action,
  // fetched with limit=1 so we never download the rows themselves. Stable as
  // the user paginates. When an action filter is active, the other two are 0 so
  // the three cards still sum to the "Matched" total.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const base = buildFilters();
      const countFor = async (action: AuditAction): Promise<number> => {
        if (actionFilter && actionFilter !== action) return 0;
        const r = await auditService.list({ ...base, action, page: 1, limit: 1 });
        return r.meta.total;
      };
      try {
        const [creates, updates, deletes] = await Promise.all([
          countFor('CREATE'), countFor('UPDATE'), countFor('DELETE'),
        ]);
        if (!cancelled) setActionCounts({ creates, updates, deletes });
      } catch {
        /* KPI cards are directional; keep prior counts on failure */
      }
    })();
    return () => { cancelled = true; };
  }, [buildFilters, actionFilter]);

  const handleExport = useCallback(async (format: ReportExportFormat) => {
    if (format === 'whatsapp' || format === 'email') {
      const summary = `${totalItems.toLocaleString('en-IN')} audit entries`
        + (actionFilter ? ` • ${actionFilter}` : '')
        + (entityFilter ? ` • ${entityFilter}` : '')
        + (dateFrom || dateTo ? ` • ${dateFrom || '…'} → ${dateTo || '…'}` : '');
      shareReportLink(format, 'Orio360 Audit Logs', summary);
      return;
    }

    if (format !== 'csv') return;
    setIsExporting(true);
    try {
      const rows = await auditService.exportAll(buildFilters());
      if (rows.length === 0) {
        toast({ title: 'No audit entries to export', variant: 'warning' });
        return;
      }
      const stamp = new Date().toISOString().slice(0, 10);
      // Export metadata only — no `changes` payload, since redacted values
      // would still leak structural context for sensitive operations.
      downloadCsv(
        `audit-logs-${stamp}.csv`,
        [
          { header: 'Timestamp (IST)', get: (r: AuditEntry) => formatTimestamp(r.created_at) },
          { header: 'User', get: (r: AuditEntry) => r.user_name ?? '' },
          { header: 'Role', get: (r: AuditEntry) => r.user_role ?? '' },
          { header: 'Action', get: (r: AuditEntry) => r.action },
          { header: 'Entity Type', get: (r: AuditEntry) => r.entity_type },
          { header: 'Entity ID', get: (r: AuditEntry) => r.entity_id ?? '' },
          { header: 'Tenant ID', get: (r: AuditEntry) => r.tenant_id ?? '' },
          { header: 'IP Address', get: (r: AuditEntry) => r.ip_address ?? '' },
          { header: 'API Path', get: (r: AuditEntry) => r.path ?? '' },
          { header: 'Duration (ms)', get: (r: AuditEntry) => r.duration_ms?.toString() ?? '' },
        ],
        rows,
      );
      toast({ title: `Exported ${rows.length.toLocaleString('en-IN')} audit entries`, variant: 'success' });
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : 'Export failed', variant: 'error' });
    } finally {
      setIsExporting(false);
    }
  }, [buildFilters, totalItems, actionFilter, entityFilter, dateFrom, dateTo, toast]);

  const columns: ColumnDef<AuditEntry>[] = useMemo(() => {
    const cols: ColumnDef<AuditEntry>[] = [
      {
        key: 'created_at', header: 'Timestamp',
        render: (_v: unknown, row: AuditEntry) => (
          <span className="text-xs font-mono tabular-nums text-neutral-500 dark:text-neutral-400 whitespace-nowrap">
            {formatTimestamp(row.created_at)}
          </span>
        ),
      },
      {
        key: 'user_name', header: 'User',
        render: (_v: unknown, row: AuditEntry) => (
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-sm font-medium text-neutral-700 dark:text-neutral-200 truncate">{row.user_name ?? '—'}</span>
            {row.user_role ? (
              <span className={cn('text-[10px] uppercase tracking-wider font-semibold rounded px-1.5 py-0.5 whitespace-nowrap', roleTone(row.user_role))}>
                {row.user_role.replace('_', ' ')}
              </span>
            ) : null}
          </div>
        ),
      },
      {
        key: 'action', header: 'Action',
        render: (_v: unknown, row: AuditEntry) => {
          const badge = ACTION_BADGE[row.action] ?? ACTION_BADGE.READ;
          return <Badge variant={badge.variant} size="sm">{row.action}</Badge>;
        },
      },
      {
        key: 'entity_type', header: 'Entity',
        render: (_v: unknown, row: AuditEntry) => (
          <span className="text-xs font-medium text-neutral-600 dark:text-neutral-300 capitalize">
            {row.entity_type.toLowerCase().replace('_', ' ')}
          </span>
        ),
      },
      {
        key: 'entity_id', header: 'Reference',
        render: (_v: unknown, row: AuditEntry) => row.entity_id ? (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); copyToClipboard(row.entity_id!, 'Entity ID'); }}
            className="group inline-flex items-center gap-1 text-xs font-mono tabular-nums text-neutral-500 dark:text-neutral-400 hover:text-brand-600 dark:hover:text-brand-300 transition-colors"
            aria-label="Copy entity ID"
          >
            <span>{row.entity_id.slice(0, 8)}…</span>
            <CopyIcon className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
          </button>
        ) : (
          <span className="text-xs text-neutral-400">—</span>
        ),
      },
    ];

    if (isPlatformRole) {
      const tenantMap = new Map(tenants.map((t) => [t.id, t.business_name]));
      cols.splice(2, 0, {
        key: 'tenant_id', header: 'Tenant',
        render: (_v: unknown, row: AuditEntry) => {
          if (!row.tenant_id) return <span className="text-xs text-neutral-400">Platform</span>;
          const name = tenantMap.get(row.tenant_id);
          return (
            <div className="min-w-0">
              <p className="text-xs font-medium text-neutral-700 dark:text-neutral-200 truncate">{name ?? 'Unknown'}</p>
              <p className="text-[10px] font-mono text-neutral-400 dark:text-neutral-500 truncate">{row.tenant_id.slice(0, 8)}…</p>
            </div>
          );
        },
      });
    }

    cols.push({
      key: 'id', header: '',
      render: (_v: unknown, row: AuditEntry) => (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); openDetail(row); }}
          aria-label="View audit details"
          className="text-xs font-medium text-brand-600 dark:text-brand-300 hover:text-brand-800 dark:hover:text-brand-100 transition-colors"
        >
          Details →
        </button>
      ),
    });

    return cols;
  }, [openDetail, copyToClipboard, isPlatformRole, tenants]);

  const totalPages = Math.ceil(totalItems / PAGE_SIZE);

  const userOptions = useMemo(() => [
    { value: '', label: 'All Users' },
    ...users.map((u) => ({ value: u.id, label: u.name })),
  ], [users]);

  const tenantOptions = useMemo(() => [
    { value: '', label: 'All Tenants' },
    ...tenants.map((t) => ({ value: t.id, label: t.business_name })),
  ], [tenants]);

  const activeFilterCount = [
    actionFilter, entityFilter, userFilter, entityIdFilter,
    isPlatformRole ? tenantFilter : '', dateFrom, dateTo,
  ].filter(Boolean).length;

  // ─── Filter controls (reused in filter bar + bottom sheet) ───────────────
  // A render function, not a component: a component declared inside the page is a
  // new type on every render, so React remounted the Selects and an open dropdown
  // snapped shut whenever the list, KPI counts or user options arrived (QA 1.7.15).

  const renderFilterControls = (stacked = false) => (
    <div className={cn(stacked ? 'flex flex-col gap-3' : 'flex flex-wrap items-center gap-3')}>
      {isPlatformRole && (
        <div className={stacked ? 'w-full' : 'w-52'}>
          <Select
            options={tenantOptions}
            value={tenantFilter}
            onChange={(v) => { setTenantFilter(typeof v === 'string' ? v : v[0] ?? ''); setPage(1); }}
            placeholder="All Tenants"
          />
        </div>
      )}
      <div className={stacked ? 'w-full' : 'w-40'}>
        <Select
          options={ENTITY_TYPE_OPTIONS}
          value={entityFilter}
          onChange={(v) => { setEntityFilter(typeof v === 'string' ? v : v[0] ?? ''); setPage(1); }}
          placeholder="All Entities"
        />
      </div>
      <div className={stacked ? 'w-full' : 'w-36'}>
        <Select
          options={ACTION_FILTER_OPTIONS}
          value={actionFilter}
          onChange={(v) => { setActionFilter(typeof v === 'string' ? v : v[0] ?? ''); setPage(1); }}
          placeholder="All Actions"
        />
      </div>
      {!isOwnActionsOnly && (
        <div className={stacked ? 'w-full' : 'w-48'}>
          <Select
            options={userOptions}
            value={userFilter}
            onChange={(v) => { setUserFilter(typeof v === 'string' ? v : v[0] ?? ''); setPage(1); }}
            placeholder="All Users"
          />
        </div>
      )}
      <div className={stacked ? 'w-full' : 'w-56'}>
        <Input
          variant="search"
          size="sm"
          value={entityIdFilter}
          onChange={(v) => { setEntityIdFilter(v); setPage(1); }}
          placeholder="Entity UUID…"
          inputClassName="font-mono"
          aria-label="Filter by entity UUID"
        />
      </div>
      <div className={cn('flex items-center gap-2', stacked && 'w-full')}>
        <span className="text-[10px] uppercase tracking-wider font-semibold text-neutral-400 dark:text-neutral-500">From</span>
        <DatePicker
          value={dateFrom ? new Date(dateFrom + 'T00:00:00') : undefined}
          onChange={(d: Date) => { setDateFrom(d.toISOString().split('T')[0]); setActivePreset(''); setPage(1); }}
        />
      </div>
      <div className={cn('flex items-center gap-2', stacked && 'w-full')}>
        <span className="text-[10px] uppercase tracking-wider font-semibold text-neutral-400 dark:text-neutral-500">To</span>
        <DatePicker
          value={dateTo ? new Date(dateTo + 'T00:00:00') : undefined}
          onChange={(d: Date) => { setDateTo(d.toISOString().split('T')[0]); setActivePreset(''); setPage(1); }}
        />
      </div>
    </div>
  );

  // ─── Active-filter chips ───────────────────────────────────────────────

  const activeChips: Array<{ key: string; label: string; onClear: () => void }> = [];
  if (actionFilter) activeChips.push({ key: 'action', label: `Action: ${actionFilter}`, onClear: () => { setActionFilter(''); setPage(1); } });
  if (entityFilter) activeChips.push({ key: 'entity', label: `Entity: ${entityFilter}`, onClear: () => { setEntityFilter(''); setPage(1); } });
  if (userFilter) {
    const u = users.find((x) => x.id === userFilter);
    activeChips.push({ key: 'user', label: `User: ${u?.name ?? userFilter}`, onClear: () => { setUserFilter(''); setPage(1); } });
  }
  if (entityIdFilter) activeChips.push({ key: 'eid', label: `ID: ${entityIdFilter.slice(0, 8)}…`, onClear: () => { setEntityIdFilter(''); setPage(1); } });
  if (isPlatformRole && tenantFilter) {
    const t = tenants.find((x) => x.id === tenantFilter);
    activeChips.push({ key: 'tenant', label: `Tenant: ${t?.business_name ?? tenantFilter}`, onClear: () => { setTenantFilter(''); setPage(1); } });
  }
  if (dateFrom) activeChips.push({ key: 'from', label: `From ${dateFrom}`, onClear: () => { setDateFrom(''); setActivePreset(''); setPage(1); } });
  if (dateTo) activeChips.push({ key: 'to', label: `To ${dateTo}`, onClear: () => { setDateTo(''); setActivePreset(''); setPage(1); } });

  const presets: Array<{ value: Preset; label: string }> = [
    { value: 'today', label: 'Today' },
    { value: '7d',    label: '7 days' },
    { value: '30d',   label: '30 days' },
    { value: '90d',   label: '90 days' },
    { value: 'month', label: 'This month' },
  ];

  const actionBadgeForEntry = selectedEntry ? (ACTION_BADGE[selectedEntry.action] ?? ACTION_BADGE.READ) : null;
  const relatedHref = selectedEntry && selectedEntry.entity_id && ENTITY_ROUTE[selectedEntry.entity_type]
    ? ENTITY_ROUTE[selectedEntry.entity_type](selectedEntry.entity_id)
    : null;

  return (
    <PageContainer
      title="Audit Logs"
      subtitle="Immutable trail of all create, update, and delete operations"
      breadcrumbs={[{ label: 'Home', href: '/home' }, { label: 'Audit Logs' }]}
    >
      <div className="animate-fade-in">
        {/* ─── Header — security console aesthetic ─────────────────────── */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-indigo-600 via-violet-600 to-fuchsia-600 p-6 text-white shadow-[0_25px_70px_-25px_rgba(139,92,246,0.6)] mb-6">
          {/* Subtle security-grid pattern overlay */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 opacity-[0.08]"
            style={{
              backgroundImage:
                'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.6) 1px, transparent 0)',
              backgroundSize: '24px 24px',
            }}
          />
          <div className="pointer-events-none absolute -top-24 -right-24 h-64 w-64 rounded-full bg-indigo-500/10 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-32 -left-20 h-72 w-72 rounded-full bg-sky-500/10 blur-3xl" />

          <div className="relative flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex items-start gap-3">
              <div className="h-11 w-11 flex items-center justify-center rounded-xl bg-white/5 ring-1 ring-white/10 shadow-inner">
                <ShieldIcon className="h-5 w-5 text-indigo-300" />
              </div>
              <div>
                <h1 className="text-2xl font-bold tracking-tight text-white">Audit Logs</h1>
                <p className="text-white/85 mt-1 text-sm max-w-md">
                  Compliance-grade trail of every create, update, and delete — immutable, timestamped, and retained for 3 years (ADR-012).
                </p>
                {isOwnActionsOnly && (
                  <span
                    data-testid="own-actions-notice"
                    className="inline-flex items-center gap-1.5 mt-2 rounded-full bg-white/10 ring-1 ring-white/25 px-2.5 py-1 text-xs font-medium text-white/90"
                  >
                    <ShieldIcon className="h-3.5 w-3.5 text-indigo-200" />
                    Showing your own actions only — the owner sees the full tenant history
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <ReportExportMenu onExport={handleExport} disabled={isLoading || isExporting} />
            </div>
          </div>

          {/* KPI strip */}
          <div className="relative mt-5 grid grid-cols-2 sm:grid-cols-4 gap-px rounded-xl bg-white/30 ring-1 ring-white/40 overflow-hidden">
            <KpiCell
              label="Matched"
              value={totalItems.toLocaleString('en-IN')}
              hint="All filters"
              strip="from-white/10 via-white/5 to-transparent"
            />
            <KpiCell
              label="Creates"
              value={actionCounts.creates.toLocaleString('en-IN')}
              hint="All filters"
              strip={ACTION_BADGE.CREATE.strip}
            />
            <KpiCell
              label="Updates"
              value={actionCounts.updates.toLocaleString('en-IN')}
              hint="All filters"
              strip={ACTION_BADGE.UPDATE.strip}
            />
            <KpiCell
              label="Deletes"
              value={actionCounts.deletes.toLocaleString('en-IN')}
              hint="All filters"
              strip={ACTION_BADGE.DELETE.strip}
            />
          </div>
        </div>

        {/* ─── Filter bar — desktop ───────────────────────────────────── */}
        <div className={cn(GLASS_FILTER, 'p-4 mb-3 hidden sm:block')}>
          {/* Preset row */}
          <div className="flex items-center gap-2 flex-wrap mb-3">
            <span className="text-[10px] uppercase tracking-wider font-semibold text-neutral-500 dark:text-neutral-400 mr-1">Quick range</span>
            {presets.map((p) => (
              <button
                key={p.value}
                type="button"
                onClick={() => applyPreset(p.value)}
                className={cn(
                  'px-2.5 h-7 rounded-full text-xs font-medium transition-all duration-150',
                  activePreset === p.value
                    ? 'bg-gradient-to-r from-brand-600 to-indigo-600 text-white shadow-[0_4px_14px_-6px_rgba(79,70,229,0.55)]'
                    : 'bg-white/60 dark:bg-neutral-800/60 text-neutral-600 dark:text-neutral-300 hover:bg-white dark:hover:bg-neutral-800',
                )}
                aria-pressed={activePreset === p.value}
              >
                {p.label}
              </button>
            ))}
            <div className="flex-1" />
            <Button
              variant="ghost"
              size="sm"
              onClick={resetFilters}
              disabled={activeFilterCount === 0}
              aria-label="Reset all filters"
            >
              Reset all
            </Button>
          </div>

          {/* Control row */}
          {renderFilterControls()}
        </div>

        {/* ─── Filter trigger — mobile ───────────────────────────────── */}
        <div className="flex sm:hidden items-center justify-between gap-2 mb-3">
          <button
            type="button"
            onClick={() => setFilterSheetOpen(true)}
            className={cn(
              GLASS_FILTER,
              'flex-1 flex items-center gap-2 px-4 py-3 text-sm font-medium text-neutral-700 dark:text-neutral-300',
            )}
          >
            <FilterIcon className="h-4 w-4 text-neutral-500" />
            <span>Filters</span>
            {activeFilterCount > 0 && (
              <span className="ml-auto h-5 min-w-[20px] px-1 flex items-center justify-center rounded-full bg-gradient-to-r from-brand-600 to-indigo-600 text-white text-[10px] font-semibold">
                {activeFilterCount}
              </span>
            )}
          </button>
          {activeFilterCount > 0 && (
            <Button variant="ghost" size="sm" onClick={resetFilters}>Reset</Button>
          )}
        </div>

        {/* ─── Active filter chips ───────────────────────────────────── */}
        {activeChips.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4 animate-fade-in">
            {activeChips.map((c) => (
              <button
                key={c.key}
                type="button"
                onClick={c.onClear}
                className="group inline-flex items-center gap-1.5 px-2.5 h-7 rounded-full bg-brand-50 dark:bg-brand-900/20 text-brand-700 dark:text-brand-200 text-xs font-medium transition-colors hover:bg-brand-100 dark:hover:bg-brand-900/40"
              >
                <span>{c.label}</span>
                <span className="text-brand-400 group-hover:text-brand-600 dark:group-hover:text-brand-100" aria-hidden="true">✕</span>
              </button>
            ))}
          </div>
        )}

        {/* ─── Results ───────────────────────────────────────────────── */}
        {isLoading ? (
          <div className={cn(GLASS, 'p-6 space-y-3')}>
            {Array.from({ length: 20 }).map((_, i) => (
              <div key={i} className="flex gap-4">
                <Skeleton variant="text" width="18%" height={16} />
                <Skeleton variant="text" width="15%" height={16} />
                <Skeleton variant="rect" width={60} height={22} />
                <Skeleton variant="text" width="10%" height={16} />
                <Skeleton variant="text" width="10%" height={16} />
              </div>
            ))}
          </div>
        ) : entries.length === 0 ? (
          <EmptyState
            variant="page"
            icon={<ShieldEmptyIcon />}
            title="No audit entries for selected filters"
            description="Adjust your filters or date range to find audit records"
          />
        ) : (
          <>
            <div className={cn(GLASS, 'overflow-hidden hidden sm:block mb-6')}>
              <div className="px-5 py-4 flex items-center justify-between">
                <h3 className="text-[11px] font-bold text-neutral-600 dark:text-neutral-300 uppercase tracking-[0.14em]">
                  Entries
                </h3>
                <p className="text-xs font-mono tabular-nums text-neutral-500 dark:text-neutral-400">
                  {totalItems.toLocaleString('en-IN')} total
                  {totalPages > 1 && <> · page {page} of {totalPages}</>}
                </p>
              </div>
              <DataTable<AuditEntry> columns={columns} data={entries} />
            </div>

            <div className="block sm:hidden space-y-3 mb-6">
              {entries.map((entry) => (
                <AuditMobileCard key={entry.id} entry={entry} onClick={() => openDetail(entry)} />
              ))}
            </div>

            {totalPages > 1 && (
              <div className="mb-6">
                <Pagination
                  currentPage={page}
                  totalPages={totalPages}
                  totalItems={totalItems}
                  pageSize={PAGE_SIZE}
                  onPageChange={setPage}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* ─── Mobile Filter Bottom Sheet ──────────────────────────────── */}
      <Drawer
        open={filterSheetOpen}
        onClose={() => setFilterSheetOpen(false)}
        title="Filters"
        position="bottom"
      >
        <div className="space-y-4 p-1">
          <div>
            <p className="text-[10px] uppercase tracking-wider font-semibold text-neutral-500 dark:text-neutral-400 mb-2">Quick range</p>
            <div className="flex flex-wrap gap-2">
              {presets.map((p) => (
                <button
                  key={p.value}
                  type="button"
                  onClick={() => applyPreset(p.value)}
                  className={cn(
                    'px-3 h-8 rounded-full text-xs font-medium transition-all',
                    activePreset === p.value
                      ? 'bg-gradient-to-r from-brand-600 to-indigo-600 text-white'
                      : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300',
                  )}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          {renderFilterControls(true)}
          <div className="flex items-center gap-2 pt-2">
            <Button variant="ghost" size="sm" onClick={resetFilters} disabled={activeFilterCount === 0}>
              Reset
            </Button>
            <Button variant="primary" size="sm" onClick={() => setFilterSheetOpen(false)} className="flex-1">
              Show results
            </Button>
          </div>
        </div>
      </Drawer>

      {/* ─── Detail Drawer ─────────────────────────────────────────── */}
      <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)} title="Audit Entry">
        {selectedEntry && actionBadgeForEntry && (
          <div className="space-y-5 p-1">
            {/* Action-aware header strip */}
            <div className={cn('rounded-xl p-4 bg-gradient-to-r', actionBadgeForEntry.strip)}>
              <div className="flex items-center gap-2 mb-2">
                <Badge variant={actionBadgeForEntry.variant} size="sm">{selectedEntry.action}</Badge>
                <span className="text-xs font-medium text-neutral-700 dark:text-neutral-200 capitalize">
                  {selectedEntry.entity_type.toLowerCase().replace('_', ' ')}
                </span>
              </div>
              <p className="text-xs text-neutral-600 dark:text-neutral-300">
                by <span className="font-medium text-neutral-800 dark:text-neutral-100">{selectedEntry.user_name ?? 'Unknown'}</span>
                {selectedEntry.user_role ? (
                  <> · <span className={cn('text-[10px] uppercase tracking-wider font-semibold rounded px-1.5 py-0.5', roleTone(selectedEntry.user_role))}>
                    {selectedEntry.user_role.replace('_', ' ')}
                  </span></>
                ) : null}
                <> · <span className="font-mono tabular-nums">{formatTimestamp(selectedEntry.created_at)}</span></>
              </p>
            </div>

            {/* Metadata grid */}
            <div className="rounded-xl bg-neutral-50 dark:bg-neutral-900/50 p-4">
              <h4 className="text-[10px] font-bold text-neutral-500 dark:text-neutral-400 uppercase tracking-[0.14em] mb-3">Metadata</h4>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-xs">
                {selectedEntry.entity_id && (
                  <div>
                    <dt className="text-neutral-500 dark:text-neutral-400">Entity ID</dt>
                    <dd className="mt-0.5 flex items-center gap-1.5">
                      <span className="font-mono tabular-nums text-neutral-700 dark:text-neutral-200 break-all">{selectedEntry.entity_id}</span>
                      <button
                        type="button"
                        onClick={() => copyToClipboard(selectedEntry.entity_id!, 'Entity ID')}
                        className="text-neutral-400 hover:text-brand-600 dark:hover:text-brand-300 transition-colors shrink-0"
                        aria-label="Copy entity ID"
                      >
                        <CopyIcon />
                      </button>
                    </dd>
                  </div>
                )}
                {isPlatformRole && selectedEntry.tenant_id && (
                  <div>
                    <dt className="text-neutral-500 dark:text-neutral-400">Tenant</dt>
                    <dd className="mt-0.5 font-mono tabular-nums text-neutral-700 dark:text-neutral-200 break-all">{selectedEntry.tenant_id}</dd>
                  </div>
                )}
                <div>
                  <dt className="text-neutral-500 dark:text-neutral-400">IP Address</dt>
                  <dd className="mt-0.5 font-mono text-neutral-700 dark:text-neutral-200">{selectedEntry.ip_address ?? '—'}</dd>
                </div>
                {selectedEntry.path && (
                  <div className="sm:col-span-2">
                    <dt className="text-neutral-500 dark:text-neutral-400">API Path</dt>
                    <dd className="mt-0.5 font-mono text-neutral-700 dark:text-neutral-200 break-all">{selectedEntry.path}</dd>
                  </div>
                )}
                {selectedEntry.duration_ms != null && (
                  <div>
                    <dt className="text-neutral-500 dark:text-neutral-400">Duration</dt>
                    <dd className="mt-0.5 font-mono tabular-nums text-neutral-700 dark:text-neutral-200">{selectedEntry.duration_ms} ms</dd>
                  </div>
                )}
              </dl>
              {relatedHref && (
                <Link
                  to={relatedHref}
                  onClick={() => setDrawerOpen(false)}
                  className="mt-4 inline-flex items-center gap-1 text-xs font-medium text-brand-600 dark:text-brand-300 hover:text-brand-800 dark:hover:text-brand-100 transition-colors"
                >
                  Open {selectedEntry.entity_type.toLowerCase()}
                  <ExternalLinkIcon />
                </Link>
              )}
            </div>

            {/* Diff (when new_value is present and action is UPDATE) */}
            {selectedEntry.action === 'UPDATE' && selectedEntry.new_value && selectedEntry.changes && (
              <div>
                <h4 className="text-[10px] font-bold text-neutral-500 dark:text-neutral-400 uppercase tracking-[0.14em] mb-3">Changes</h4>
                <DiffView oldValue={selectedEntry.changes} newValue={selectedEntry.new_value} />
              </div>
            )}

            {/* Before → after (when the handler recorded a before-snapshot) */}
            {selectedEntry.old_value && (
              <div>
                <h4 className="text-[10px] font-bold text-neutral-500 dark:text-neutral-400 uppercase tracking-[0.14em] mb-3">Before → After</h4>
                <DiffView
                  oldValue={selectedEntry.old_value}
                  newValue={Object.fromEntries(
                    Object.keys(selectedEntry.old_value).map((k) => [k, selectedEntry.new_value?.[k]]),
                  )}
                />
              </div>
            )}

            {/* Payload tree */}
            <div>
              <h4 className="text-[10px] font-bold text-neutral-500 dark:text-neutral-400 uppercase tracking-[0.14em] mb-3">
                Recorded Payload
              </h4>
              <PayloadTree payload={selectedEntry.new_value ?? selectedEntry.changes} />
            </div>
          </div>
        )}
      </Drawer>
    </PageContainer>
  );
}

// ─── KPI Cell (header strip) ──────────────────────────────────────────────

function KpiCell({
  label,
  value,
  hint,
  strip,
}: { label: string; value: string; hint: string; strip: string }) {
  return (
    <div className={cn('relative px-4 py-3 bg-white overflow-hidden')}>
      <div className={cn('absolute inset-x-0 bottom-0 h-10 opacity-60 bg-gradient-to-t pointer-events-none', strip)} />
      <p className="relative text-[10px] uppercase tracking-wider font-semibold text-violet-500">{label}</p>
      <p className="relative text-xl font-bold font-mono tabular-nums text-violet-700 mt-0.5">{value}</p>
      <p className="relative text-[10px] text-neutral-500 mt-0.5">{hint}</p>
    </div>
  );
}

export function AuditLogsPage() {
  return (
    <ErrorBoundary>
      <AuditLogsContent />
    </ErrorBoundary>
  );
}
